import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Star, CheckCircle, Target, Sparkles, Clock, User, Heart, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ExactPrediction = () => {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPredictions();
  }, []);

  const fetchPredictions = async () => {
    try {
      setLoading(true);
      // Fetch all predictions from consolidated API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exact-prediction/all`);
      const data = await response.json();
      setPredictions(data.predictions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const prediction = predictions[currentIndex];
  
  const nextPrediction = () => {
    if (predictions.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % predictions.length);
    }
  };
  
  const prevPrediction = () => {
    if (predictions.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + predictions.length) % predictions.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Calculating exact prediction...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-red-900/30 border-red-500/50 max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-400">Error: {error}</p>
            <Button onClick={fetchPredictions} className="mt-4">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="text-white/70 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="text-center">
          <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            EXACT DATE PREDICTION
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {prediction?.person?.name || 'Loading...'}
          </h1>
          <p className="text-purple-300">
            Marriage Date Prediction with Day-Level Accuracy
          </p>
          
          {/* Navigation buttons */}
          {predictions.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button variant="outline" size="sm" onClick={prevPrediction} className="text-white border-white/30">
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-white/60">{currentIndex + 1} / {predictions.length}</span>
              <Button variant="outline" size="sm" onClick={nextPrediction} className="text-white border-white/30">
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {prediction && (
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Accuracy Banner */}
          <Card className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Target className="w-8 h-8 text-green-400" />
                  <div>
                    <p className="text-green-400 font-bold text-2xl">{prediction.prediction?.accuracy}</p>
                    <p className="text-green-300/70 text-sm">Prediction Accuracy</p>
                  </div>
                </div>
                <div className="h-12 w-px bg-green-500/30 hidden md:block"></div>
                <div className="text-center">
                  <p className="text-white font-semibold">Predicted: {prediction.prediction?.predicted_date}</p>
                  <p className="text-green-300">Actual: {prediction.actual_marriage?.date}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Person Details */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-purple-400" />
                Birth Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Birth Date</p>
                  <p className="text-white font-medium">{prediction.person?.birth_date}</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Birth Time</p>
                  <p className="text-white font-medium">{prediction.person?.birth_time}</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Birth Place</p>
                  <p className="text-white font-medium">{prediction.person?.birth_place}</p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Lagna</p>
                  <p className="text-purple-400 font-medium">{prediction.person?.lagna}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marriage Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" />
                  Actual Marriage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Date</span>
                  <span className="text-white font-medium">{prediction.actual_marriage?.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Age</span>
                  <span className="text-white font-medium">{prediction.actual_marriage?.age} years</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Spouse</span>
                  <span className="text-white font-medium">{prediction.actual_marriage?.spouse}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Algorithm Prediction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-purple-300">Predicted Date</span>
                  <span className="text-white font-bold">{prediction.prediction?.predicted_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-300">Year</span>
                  <span className="text-white font-medium">{prediction.prediction?.predicted_year}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-300">Accuracy</span>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    {prediction.prediction?.accuracy}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Astrological Factors */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Astrological Factors
              </CardTitle>
              <CardDescription className="text-slate-400">
                Key factors that determined the exact marriage date
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dasha Info */}
              <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
                <h4 className="text-amber-400 font-semibold mb-2">Dasha Period</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-slate-400 text-sm">Active Dasha</p>
                    <p className="text-white font-medium">{prediction.astrological_factors?.dasha}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Mahadasha Lord</p>
                    <p className="text-white font-medium">{prediction.astrological_factors?.mahadasha_lord}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Antardasha Lord</p>
                    <p className="text-white font-medium">{prediction.astrological_factors?.antardasha_lord}</p>
                  </div>
                </div>
              </div>

              {/* Transits */}
              <div>
                <h4 className="text-purple-400 font-semibold mb-3">Transits on Wedding Day</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {prediction.astrological_factors?.transits_on_wedding_day && 
                    Object.entries(prediction.astrological_factors.transits_on_wedding_day).map(([planet, position]) => (
                      <div key={planet} className="bg-slate-900/50 p-3 rounded-lg flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                          <span className="text-purple-400 font-bold text-sm">{planet.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{planet}</p>
                          <p className="text-slate-400 text-sm">{position}</p>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Key Alignments */}
              <div>
                <h4 className="text-green-400 font-semibold mb-3">Key Alignments</h4>
                <div className="space-y-2">
                  {prediction.astrological_factors?.key_alignments?.map((alignment, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <p className="text-slate-300">{alignment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Methodology */}
          <Card className="bg-slate-800/50 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Prediction Methodology
              </CardTitle>
              <CardDescription className="text-slate-400">
                Step-by-step algorithm used to arrive at the exact date
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {prediction.methodology && Object.entries(prediction.methodology).map(([step, description], idx) => (
                  <div key={step} className="flex gap-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{step.replace(/_/g, ' ').toUpperCase()}</p>
                      <p className="text-slate-400 text-sm">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Success Message */}
          <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30">
            <CardContent className="pt-6 text-center">
              <Sparkles className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Algorithm Successfully Predicted Marriage Date
              </h3>
              <p className="text-purple-200">
                The self-feedback astrology system predicted {prediction?.person?.name}'s marriage date 
                with <span className="text-green-400 font-bold">day-level accuracy</span>
                {prediction?.prediction?.accuracy?.includes("EXACT") ? (
                  <span> - an <span className="text-yellow-400 font-bold">EXACT MATCH</span>!</span>
                ) : (
                  <span>, being {prediction?.prediction?.accuracy?.toLowerCase()}.</span>
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ExactPrediction;
